import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  KeyRound,
  Mail,
  MessageSquare,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import Input from '../../components/ui/Input';
import { resendLeadCode, submitLead, verifyLeadCode, verifyLeadToken } from '../../api/leads';
import { getApiErrorMessage } from '../../utils/errors';
import { COMPANY_BRAND_NAME } from '../../support/brand';

function formatPhone(value) {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
}

function LeadCapture() {
  const [step, setStep] = useState('form'); // 'form' | '2fa' | 'success' | 'validating_token'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [code, setCode] = useState('');
  const [leadId, setLeadId] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState('');

  // Verifica se a URL contém um token de validação (?token=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) return;

    let isMounted = true;
    setStep('validating_token');

    verifyLeadToken(token)
      .then(() => {
        if (isMounted) setStep('success');
      })
      .catch((err) => {
        if (isMounted) {
          setError(getApiErrorMessage(err, 'Link de verificação inválido ou expirado.'));
          setStep('form');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Cronômetro para o botão de reenvio de código
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const interval = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handlePhoneChange = (e) => {
    setPhone(formatPhone(e.target.value));
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setError('');

    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 10 || cleanPhone.length > 11) {
      setError('Por favor, informe seu telefone com DDD válido (10 ou 11 dígitos).');
      return;
    }

    if (cleanPhone.length === 11 && cleanPhone[2] !== '9') {
      setError('Telefone celular deve iniciar com o dígito 9 após o DDD.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await submitLead({
        name: name.trim(),
        email: email.trim(),
        phone: cleanPhone,
        honeypot: honeypot.trim(),
      });
      setLeadId(response.lead_id);
      setStep('2fa');
      setResendCooldown(60);
      setCode('');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível registrar seu contato no momento.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');

    const cleanCode = code.replace(/\D/g, '');
    if (cleanCode.length < 4) {
      setError('Por favor, informe o código de verificação recebido.');
      return;
    }

    setIsSubmitting(true);

    try {
      await verifyLeadCode(leadId, cleanCode);
      setStep('success');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Código incorreto ou expirado. Verifique e tente novamente.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || !leadId) return;
    setError('');
    setResendSuccess('');
    setIsSubmitting(true);

    try {
      await resendLeadCode(leadId);
      setResendSuccess('Código reenviado com sucesso para seu WhatsApp e e-mail.');
      setResendCooldown(60);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível reenviar o código.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === 'validating_token') {
    return (
      <div className="surface-card p-8 sm:p-10 shadow-card-md text-center py-12">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-forge-100 text-forge-600 animate-spin mb-4">
          <RefreshCw className="w-6 h-6" />
        </div>
        <h2 className="text-xl font-bold text-content-primary mb-2">Validando confirmação...</h2>
        <p className="text-content-muted text-sm">Aguarde um momento enquanto ativamos seu cadastro.</p>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="surface-card p-8 sm:p-10 shadow-card-md">
        <div className="text-center py-4 space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 mb-2">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <h2 className="text-2xl font-bold text-content-primary">
            Cadastro verificado com sucesso!
          </h2>
          <p className="text-content-secondary text-sm leading-relaxed max-w-sm mx-auto">
            {name ? `Obrigado, ${name.split(' ')[0]}!` : 'Obrigado!'} Suas informações foram validadas. Nossa equipe entrará em contato em breve pelo WhatsApp ou e-mail.
          </p>
          <div className="pt-6">
            <Link to="/" className="btn-primary inline-flex items-center gap-2">
              Voltar para o início
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (step === '2fa') {
    return (
      <div className="surface-card p-8 sm:p-10 shadow-card-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-forge-50 border border-forge-200 text-xs font-semibold text-forge-700 uppercase tracking-wider mb-3">
            <KeyRound className="w-3.5 h-3.5" />
            Confirmação de Segurança
          </div>
          <h1 className="text-2xl font-bold text-content-primary mb-2">
            Digite o código de 6 dígitos
          </h1>
          <p className="text-content-muted text-sm max-w-sm mx-auto leading-relaxed">
            Enviamos um código no seu WhatsApp e um link de confirmação para o seu e-mail.
          </p>
        </div>

        <div className="mb-6 p-3.5 rounded-xl bg-surface-base border border-surface-border text-xs text-content-secondary space-y-1.5">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>WhatsApp: <strong>{phone}</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-sky-600 shrink-0" />
            <span>E-mail: <strong>{email}</strong></span>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium">
            {error}
          </div>
        )}

        {resendSuccess && (
          <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium">
            {resendSuccess}
          </div>
        )}

        <form onSubmit={handleVerifyCode} className="space-y-4">
          <div>
            <label htmlFor="otp-code" className="block text-xs font-semibold text-content-primary uppercase tracking-wider mb-1 text-center">
              Código de confirmação
            </label>
            <input
              id="otp-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              className="w-full text-center text-3xl tracking-[0.4em] font-mono py-3 px-4 border border-surface-border rounded-xl focus:border-forge-500 focus:ring-2 focus:ring-forge-200 outline-none transition-all"
              required
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting || code.length < 4}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? 'Verificando...' : 'Confirmar código'}
          </button>
        </form>

        <div className="mt-6 pt-4 border-t border-surface-border flex flex-col items-center gap-3 text-xs">
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown > 0 || isSubmitting}
            className="text-forge-600 hover:text-forge-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            {resendCooldown > 0 ? `Reenviar código em ${resendCooldown}s` : 'Não recebeu? Reenviar código'}
          </button>

          <button
            type="button"
            onClick={() => {
              setStep('form');
              setError('');
            }}
            className="text-content-muted hover:text-content-primary transition-colors"
          >
            Corrigir e-mail ou telefone
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="surface-card p-8 sm:p-10 shadow-card-md">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-forge-50 border border-forge-200 text-xs font-semibold text-forge-700 uppercase tracking-wider mb-3">
          <Sparkles className="w-3.5 h-3.5" />
          Acesso à plataforma
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-content-primary mb-2">
          Comece com a {COMPANY_BRAND_NAME}
        </h1>
        <p className="text-content-muted text-sm max-w-sm mx-auto">
          Preencha os dados abaixo para receber demonstração, tirar dúvidas e liberar o acesso do seu time.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmitForm} className="space-y-4">
        <Input
          id="lead-name"
          label="Nome completo"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ex: Maria Silva"
          required
          autoFocus
        />

        <Input
          id="lead-email"
          label="E-mail corporativo ou pessoal"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="voce@empresa.com"
          required
        />

        <Input
          id="lead-phone"
          label="Telefone celular / WhatsApp (com DDD)"
          type="tel"
          value={phone}
          onChange={handlePhoneChange}
          placeholder="(11) 98765-4321"
          required
        />

        {/* Campo Honeypot invisível para bots */}
        <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
          <label htmlFor="hp_field">Não preencha este campo se for humano</label>
          <input
            id="hp_field"
            type="text"
            tabIndex="-1"
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? 'Enviando...' : 'Receber código de confirmação'}
          </button>
        </div>

        <div className="flex items-center justify-center gap-1.5 text-xs text-content-muted pt-1">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          <span>Enviaremos um código via WhatsApp e um link por e-mail para confirmação.</span>
        </div>
      </form>

      <div className="mt-6 pt-4 border-t border-surface-border flex flex-col gap-2">
        <Link to="/login" className="text-sm text-content-muted hover:text-content-primary text-center transition-colors">
          Já tem conta? <span className="text-forge-600 font-medium">Entrar</span>
        </Link>
      </div>
    </div>
  );
}

export default LeadCapture;
